/**
 * SyncNotificationProvider
 * Sprint 6 - Real-Time Sync Notifications
 * 
 * Global provider for SSE-based sync notifications.
 * Shows toast alerts for Jira/ADO sync events.
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { CheckCircle, AlertTriangle, XCircle, Info, RefreshCw } from 'lucide-react';

const SyncNotificationContext = createContext({});

export const useSyncNotifications = () => useContext(SyncNotificationContext);

// Notification icons by type
const NotificationIcon = ({ type }) => {
  switch (type) {
    case 'success':
      return <CheckCircle className="w-5 h-5 text-green-500" />;
    case 'warning':
      return <AlertTriangle className="w-5 h-5 text-amber-500" />;
    case 'error':
      return <XCircle className="w-5 h-5 text-red-500" />;
    default:
      return <Info className="w-5 h-5 text-blue-500" />;
  }
};

// Source badge
const SourceBadge = ({ source }) => {
  const colors = {
    jira: 'bg-blue-100 text-blue-800',
    ado: 'bg-purple-100 text-purple-800',
    scopeops: 'bg-green-100 text-green-800'
  };
  
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full ${colors[source] || 'bg-gray-100 text-gray-800'}`}>
      {source?.toUpperCase()}
    </span>
  );
};

export const SyncNotificationProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [connectionError, setConnectionError] = useState(null);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Show toast for notification
  const showToast = useCallback((notification) => {
    const { type, title, message, source_system, issue_key } = notification;
    
    // Auto-dismiss duration based on type
    const duration = type === 'error' ? 8000 : 5000;
    
    const toastContent = (
      <div className="flex items-start gap-3" data-testid="sync-notification-toast">
        <NotificationIcon type={type} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm">{title}</span>
            <SourceBadge source={source_system} />
          </div>
          <p className="text-sm text-gray-600 truncate">{message}</p>
          {issue_key && (
            <p className="text-xs text-gray-400 mt-1">Issue: {issue_key}</p>
          )}
        </div>
      </div>
    );

    // Use different toast types
    switch (type) {
      case 'success':
        toast.success(toastContent, { duration });
        break;
      case 'warning':
        toast.warning(toastContent, { duration });
        break;
      case 'error':
        toast.error(toastContent, { duration });
        break;
      default:
        toast(toastContent, { duration });
    }
  }, []);

  // Connect to SSE stream
  const connect = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const url = `${API_URL}/api/notifications/sync/stream`;
      eventSourceRef.current = new EventSource(url);

      eventSourceRef.current.onopen = () => {
        setIsConnected(true);
        setConnectionError(null);
        reconnectAttempts.current = 0;
        console.log('[SyncNotifications] SSE connected');
      };

      eventSourceRef.current.addEventListener('connected', (e) => {
        console.log('[SyncNotifications] Connection confirmed', JSON.parse(e.data));
      });

      eventSourceRef.current.addEventListener('sync', (e) => {
        try {
          const notification = JSON.parse(e.data);
          console.log('[SyncNotifications] Received:', notification);
          
          // Add to notifications list
          setNotifications(prev => [notification, ...prev].slice(0, 50));
          
          // Show toast
          showToast(notification);
        } catch (err) {
          console.error('[SyncNotifications] Parse error:', err);
        }
      });

      eventSourceRef.current.addEventListener('heartbeat', () => {
        // Heartbeat received - connection is alive
      });

      eventSourceRef.current.onerror = (e) => {
        console.error('[SyncNotifications] SSE error:', e);
        setIsConnected(false);
        eventSourceRef.current?.close();
        
        // Reconnect with backoff
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          setConnectionError(`Reconnecting in ${delay / 1000}s...`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect();
          }, delay);
        } else {
          setConnectionError('Connection failed. Click to retry.');
        }
      };
    } catch (err) {
      console.error('[SyncNotifications] Connection error:', err);
      setConnectionError(err.message);
    }
  }, [API_URL, showToast]);

  // Manual reconnect
  const reconnect = useCallback(() => {
    reconnectAttempts.current = 0;
    connect();
  }, [connect]);

  // Initialize connection
  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [connect]);

  // Context value
  const value = {
    isConnected,
    connectionError,
    notifications,
    reconnect,
    clearNotifications: () => setNotifications([])
  };

  return (
    <SyncNotificationContext.Provider value={value}>
      {children}
      
      {/* Connection status indicator (optional - can be shown in header) */}
      {connectionError && (
        <button
          onClick={reconnect}
          className="fixed bottom-4 right-4 flex items-center gap-2 px-3 py-2 bg-amber-100 text-amber-800 rounded-lg shadow-lg text-sm z-50"
          data-testid="sync-reconnect-button"
        >
          <RefreshCw className="w-4 h-4" />
          {connectionError}
        </button>
      )}
    </SyncNotificationContext.Provider>
  );
};

export default SyncNotificationProvider;
